import type { ChatSessionDto } from '@/lib/api/sessions.client';
import { readScriptRun } from '@/lib/agents/script-run';
import type { IChat, IChatHistory, IMessage } from '@/lib/types/chat';

/**
 * A chat session as the chat store holds it: the opened chat built from its
 * session, the open chat refreshed by a re-read, and the sidebar row a re-read
 * may change (KAN-1266).
 */

/**
 * What a re-read session may change on a row a list already holds. The pin,
 * the share icon and the other values only the list endpoints carry stay as
 * the list has them.
 */
export const refreshedRow = (row: IChatHistory, entry: IChatHistory): IChatHistory => ({
  ...row,
  title: entry.title,
  lastMessageAt: entry.lastMessageAt,
  agentId: entry.agentId,
  agentName: entry.agentName,
  projectId: entry.projectId,
  userId: entry.userId,
});

/** The agent a session is bound to, and the live state the composer reads off it. */
const agentBindingOf = (
  session: ChatSessionDto
): Pick<
  IChat,
  'agentId' | 'agentName' | 'agentIsPaused' | 'agentPauseReason' | 'agentCanEdit'
> => ({
  agentId: session.agentId ?? null,
  agentName: session.agentName ?? null,
  agentIsPaused: session.agentIsPaused ?? false,
  agentPauseReason: session.agentPauseReason ?? null,
  agentCanEdit: session.agentCanEdit ?? false,
});

/** Whose chat it is, how the reader reached it, and whether its agent is out of reach. */
const accessOf = (
  session: ChatSessionDto
): Pick<
  IChat,
  | 'readOnly'
  | 'sharedBy'
  | 'blocked'
  | 'blockedReason'
  | 'projectId'
  | 'projectShared'
  | 'ownerUserId'
> => ({
  readOnly: session.readOnly ?? false,
  sharedBy: session.sharedBy ?? null,
  blocked: session.blocked ?? false,
  blockedReason: session.blockedReason ?? null,
  projectId: session.projectId ?? null,
  projectShared: session.projectShared ?? false,
  ownerUserId: session.userId ?? null,
});

/**
 * The opened chat as its session describes it. Without a session (both reads
 * failed) it still opens, untitled and bound to nothing.
 */
export const chatFromSession = (
  chatId: string,
  session: ChatSessionDto | null,
  messages: IMessage[],
  model: string
): IChat => {
  const known = session ?? { id: chatId, title: '' };
  return {
    id: chatId,
    title: known.title,
    messages,
    createdAt: known.createdAt ? new Date(known.createdAt) : new Date(),
    updatedAt: known.updatedAt ? new Date(known.updatedAt) : new Date(),
    model,
    isDraft: false,
    ...agentBindingOf(known),
    ...accessOf(known),
    scriptRun: readScriptRun(known.metadata),
  };
};

/**
 * The open chat with a re-read session's title and live state. The pause
 * fields carry an agent auto-paused mid-stream to the banner
 * (`ThreadAgentPausedBanner`) without a reload; the block fields do the same
 * for an agent the reader lost access to while the chat was open (KAN-1540).
 * Its binding is left alone: a read that left before the chat was taken out
 * of its project must not file it back in.
 */
export const openChatRefreshed = (chat: IChat, session: ChatSessionDto): IChat => ({
  ...chat,
  title: session.title,
  createdAt: session.createdAt ? new Date(session.createdAt) : chat.createdAt,
  updatedAt: session.updatedAt ? new Date(session.updatedAt) : chat.updatedAt,
  agentIsPaused: session.agentIsPaused ?? chat.agentIsPaused,
  agentPauseReason: session.agentPauseReason ?? chat.agentPauseReason,
  agentCanEdit: session.agentCanEdit ?? chat.agentCanEdit,
  blocked: session.blocked ?? chat.blocked,
  blockedReason: session.blockedReason ?? chat.blockedReason,
});
